import { useEffect, useState } from "react";
import { GetUserFeedback, type UserFeedbackRecord } from "@bto/shared";
import { getBaseUrl } from "../requestUtils";

export type ProviderFeedbackPromptAction =
  | "auto_open_modal"
  | "show_banner"
  | "none";

interface UseProviderFeedbackPromptEligibilityProps {
  authToken: string | undefined;
  feedbackPromptHandledStorageKey: string;
  orderedCount: number;
  orderedMin: number;
  isDesktop: boolean;
  isFeedbackModalOpen: boolean;
}

interface UseProviderFeedbackPromptEligibilityResult {
  promptAction: ProviderFeedbackPromptAction;
  isReady: boolean;
}

export const useProviderFeedbackPromptEligibility = ({
  authToken,
  feedbackPromptHandledStorageKey,
  orderedCount,
  orderedMin,
  isDesktop,
  isFeedbackModalOpen,
}: UseProviderFeedbackPromptEligibilityProps): UseProviderFeedbackPromptEligibilityResult => {
  const [hasCheckedExistingFeedback, setHasCheckedExistingFeedback] =
    useState(false);

  useEffect(() => {
    setHasCheckedExistingFeedback(false);

    if (!authToken) return;

    if (localStorage.getItem(feedbackPromptHandledStorageKey) === "true") {
      setHasCheckedExistingFeedback(true);
      return;
    }

    let isCancelled = false;

    const check = async () => {
      const response = await GetUserFeedback(await getBaseUrl(), authToken);
      if (isCancelled || !response.ok) return;

      const records = (await response.json()) as UserFeedbackRecord[];
      if (records.length > 0) {
        localStorage.setItem(feedbackPromptHandledStorageKey, "true");
      }
    };

    check()
      .then(() => {
        if (!isCancelled) setHasCheckedExistingFeedback(true);
      })
      .catch(() => undefined);

    return () => {
      isCancelled = true;
    };
  }, [authToken, feedbackPromptHandledStorageKey]);

  const hasPromptBeenHandled =
    localStorage.getItem(feedbackPromptHandledStorageKey) === "true";
  const isEligible = Boolean(authToken) && orderedCount >= orderedMin;
  const isReady = hasPromptBeenHandled || hasCheckedExistingFeedback;

  if (!isEligible || !isReady || hasPromptBeenHandled || isFeedbackModalOpen) {
    return { promptAction: "none", isReady };
  }

  return {
    promptAction: isDesktop ? "auto_open_modal" : "show_banner",
    isReady,
  };
};
